import { FilterToggleButton, useAnalytics } from '@ecosystem/newron-client-lib';
import { feedback, layouts } from '@ecosystem/newron-design-system';
import { cloneDeep, isEqual } from 'lodash';
import React, { SetStateAction, Suspense, useEffect, useMemo, useState } from 'react';
import PageHeader from '@components/page-header/PageHeader';
import { RetailerClient } from '@models/RetailerClient';
import PageInfo from '../info/PageInfo';
import TabNav, { NavigationLinkProps } from '@components/tab-nav/TabNav';
import InfoModal from '@components/info-modal/InfoModal';
import { useLocation } from 'react-router-dom';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  CountryFilterPair,
  FilterKeys,
  Filters,
  Option,
  PageName,
  PageType,
  SelectedPgCheckParam,
  SubSection,
} from '@utils/pageConfig/Types';
import {
  initialNavigationLinkConfig,
  leaderboardLink,
  productsLinkConfig,
  ProductsTabView,
} from '@components/tab-nav/TabConfig';
import { getFilters } from '@services/Filters.service';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentRetailerClientState,
  onFiltersTrayClickHandlerUtils,
  ProductDataType,
  ProductType,
  timeFrameOnChangeHandlerUtils,
} from './Product';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { isAuthorized } from '@utils/Permission';
import ContextBar from '@components/context-bar/ContextBar';
import {
  areOptionsTheSame,
  checkSelectedPgExists,
  constructCountryOptionForSideMenu,
  findDefaultFilterBySelectedCountries,
  getFeatureValueForRetailer,
  getFiltersForClient,
  getFiltersInitialState,
  getInitialAppliedCountries,
  getPeriodCountryOnChange,
  getProductGroupFromSideFilters,
  getSubscriptionPeriodicitiesByCountryAndProductType,
  removeUnsavedFilters,
  RetailerDetails,
  updateSelectedProductGroupForProducts,
  updateSortingOptions,
} from '@utils/FilterUtils';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { ClientSubscription } from '@models/Opa';
import {
  constructCountryOption,
  getSelectedClientNameWithClientId,
  getSubscriptionForOtherProducts,
  mapDefaultSubscribedCountry,
  mapSubscribedCountries,
  onChangeHandlerForOtherProducts,
  OnRetailerChangeParam,
  retainTimeFrame,
  retainTimePeriod,
  saveErrorLogForOnChange,
} from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';
import { bindActionCreators } from 'redux';
import {
  actionCreators,
  actionCreatorsHitlistsRetailerClient,
  actionCreatorsInitialFilters,
  actionCreatorsInitialFiltersProducts,
  actionCreatorsProducts,
  actionCreatorsRetailerClient,
  actionCreatorsRO,
  actionCreatorsSellerInsightsRetailerClient,
  actionCreatorsSideFilters,
} from '@state/Index';
import { constructRetailerClientState } from '@state/reducers/retailer-client/RetailerClientUtils';
import { getFeatureSelector } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import LoadingOverlay from '@components/loading-overlay/LoadingOverlay';
import { getFeatureValueByPGAndCountry } from '@services/FeatureValue.service';
import { productsResponseConfig } from '@utils/FilterSettings';
import { getProductGroupsByCountry } from '@services/ProductGroup.service';
import { updateCountryAndPGFilters } from '@utils/CountryUtils';
import { getAndStoreSavedFilters, validateFilters } from '@utils/AppSubscriptionUtils';
import Leaderboard from './leaderboard/Leaderboard';
import Trends from './trends/Trends';
import Overview from './overview/Overview';
import ProductsModelCompareProvider from '@components/products-compare-modal/ProductsModelCompareProvider';
import {
  getCorrespondingLatestPeriod,
  getInitialPeriod,
  getPeriodicityForMATAndYTD,
  isPeriodYTDOrMAT,
} from '@utils/PeriodUtils';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import { IRootState } from '@state/reducers/Reducers';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { DefaultSortingOptionsForProducts, RetailerKpiOption, SaleType } from '@models/Products';
import { PerformanceType } from '@const/PerformanceType';
import {
  FocusArea,
  KpiOptionForLabel,
  KpiOptionForValue,
  KpiTypesOption,
} from '@components/products-top-5-table/Types';
import { DisplayOptions } from '@components/top-selling-products/products-table-filter/Types';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { capitalizeFirstLetter } from '@utils/StringUtils';
import { RelativePeriod, RelativePeriodLabel } from '@components/comparison-period-selector/Types';
import { ProductsViewState, ProductTabRestoreLocalSavedViewProps } from '@models/SavedViews';
import ProductsCopyViewButton from '@components/products-copy-view-button/ProductsCopyViewButton';
import { useRestoreProductsSavedView } from '@utils/hook/saveViews/UseRestoreProductsSavedView';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { Unauthorized } from '../unauthorized/Unauthorized';
import { TrackingGeneralProp } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { useUserPersistedFilters } from '@utils/hook/saveFilter/UseUserPersistedFilters';
import { TOTAL } from '@utils/TotalUtils';
import { initialiseBaseFilters } from '@utils/InitBaseFilters';
import { useFactRevenueBySelectedCurrency } from '@hooks/UseFactRevenueBySelectedCurrency';

interface Props {
  retailerClients: RetailerClient[];
}

export const TooltipsTextComponent: React.FC = () => {
  return (
    <section>
      <p>Understand what products are driving the market and be ready to take action.</p>
      <p>
        Bringing all your product level insights into one easy to use area. Providing intuitive filter options to drill
        into the data and make reaching actionable insights from this powerful dataset easier to access than ever
        before. Track the performance of your range versus the market, review your products revenue, units and price
        position alongside relative growth and share positioning to closely monitor your range efficiency.
      </p>
      <p>
        Gain the assistance to answer your most pressing questions; <br />
        What items are selling well in the market
        <br />
        Are you selling the right items – understand what items could be a threat to your range and which you might
        consider adding
        <br />
        See the impact of new models on the market
        <br />
        Understand the price positioning of items and how you compare
        <br />
        See what features are important
        <br />
        Easily review trends to monitor your range and drive business growth and decision making.
        <br />
      </p>
    </section>
  );
};

const ProductAnalyzer: React.FC<Props> = ({ retailerClients }) => {
  const { pathname } = useLocation();
  const pagesConfig = usePageConfig();
  const { refetch } = useUserPersistedFilters();
  const isAuthorizedHitlistsUser = isAuthorized(pagesConfig.hitlistsClientSubscription);
  const isAuthorizedSellerInsightsUser = isAuthorized(pagesConfig.sellerInsightsClientSubscription);
  const isAuthorizedRetailKadUser = isAuthorized(pagesConfig.retailKadClientSubscription);
  const isAuthorizedUser = isAuthorizedHitlistsUser || isAuthorizedSellerInsightsUser || isAuthorizedRetailKadUser;
  const { Page } = layouts;
  const getProductTypeByAuthorization = () => {
    if (isAuthorizedSellerInsightsUser) {
      return ProductType.SellerInsights;
    } else if (isAuthorizedRetailKadUser) {
      return ProductType.RetailKad;
    } else {
      return ProductType.ProductAnalyzer;
    }
  };
  const currentProductType: ProductType = getProductTypeByAuthorization();

  // Redux
  const dispatch = useDispatch();
  const { updateInitialFiltersProducts } = bindActionCreators(actionCreatorsInitialFiltersProducts, dispatch);
  const { updateInitialFilters } = bindActionCreators(actionCreatorsInitialFilters, dispatch);
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);
  const { updateMainFiltersRO } = bindActionCreators(actionCreatorsRO, dispatch);
  const { updateHitlistsRetailerClient } = bindActionCreators(actionCreatorsHitlistsRetailerClient, dispatch);
  const { updateSellerInsightsRetailerClient } = bindActionCreators(
    actionCreatorsSellerInsightsRetailerClient,
    dispatch,
  );
  const { updateRetailerClient } = bindActionCreators(actionCreatorsRetailerClient, dispatch);
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);

  const [isSubscribed, setIsSubscribed] = useState<boolean>(true);

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.RI_PRODUCT);

  const hitlistsRetailerClientState = useSelector((state: any) => state.hitlistsRetailerClient);
  const sellerInsightsRetailerClientState = useSelector((state: IRootState) => state.sellerInsightsRetailerClient);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);

  const currentRetailerClientState = getCurrentRetailerClientState(
    currentProductType,
    retailerClientState,
    hitlistsRetailerClientState,
    sellerInsightsRetailerClientState,
  );

  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const mainFilterStateProducts = useSelector((state: any) => state.mainFiltersProducts);
  const initialFiltersProductsState = useSelector((state: any) => state.initialFiltersProducts);
  const initialFiltersState = useSelector((state: any) => state.initialFilters);
  const mainFilterState = useSelector((state: any) => state.mainFilters);

  const [isMultibannerStatesUpdated, setIsMultibannerStatesUpdated] = useState<boolean>(true);
  const [isMultibannerChanging, setIsMultibannerChanging] = useState<boolean>(false);

  const [defaultFilters, setDefaultFilters] = useState<MainFilters[]>(initialFiltersProductsState.initialFilters);

  const getInitialLink = (): NavigationLinkProps => {
    const tabPath = pathname.split('/').filter(Boolean).pop() ?? '';
    return productsLinkConfig.find((tab) => tab.url === tabPath) ?? initialNavigationLinkConfig;
  };

  const emptyMainFilters: MainFilters[] = [
    {
      country: '',
      timeFrame: {},
      timePeriod: {
        weekly: [],
        monthly: [],
        rollingQuarter: [],
        calendarQuarter: [],
        customMonths: [],
      },
      timePeriodV2: {
        weekly: [],
        monthly: [],
        rollingQuarter: [],
        calendarQuarter: [],
        customMonths: [],
      },
      comparisonPeriod: {},
      comparisonGrowth: {},
      factVolumeWithPeriod: [],
    },
  ];

  /*
   * Get initial state for side menu filters
   */
  const allFilters = [...pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters];
  const pgInitialState = getFiltersInitialState(allFilters, FilterKeys.PRODUCT_GROUP);

  const [selectedLink, setSelectedLink] = useState<NavigationLinkProps>(getInitialLink());
  const [selectedProductGroup, setSelectedProductGroup] = useState<Option[] | undefined>(
    pgInitialState !== undefined ? pgInitialState.options : undefined,
  );

  const { defaultCountry: initialCountry } = initialiseBaseFilters(
    pagesConfig,
    currentProductType,
    updateSideFilters,
    PageType.RI_PRODUCT,
  );
  const [appliedCountries, setAppliedCountries] = useState<Option[]>(initialCountry);
  const [selectedCountries, setSelectedCountries] = useState<Option[]>(initialCountry);

  const [selectedProdLeaderboardFeatureProductFilters, setSelectedProdLeaderboardFeatureProductFilters] = useState<
    Filters[]
  >([]);

  const [selectedProductSpecificationFilters, setSelectedProductSpecificationFilters] = useState<Filters[]>([]);

  const [appliedProdLeaderboardFeatureProductFilters, setAppliedProdLeaderboardFeatureProductFilters] = useState<
    Filters[]
  >([]);

  const [appliedProductSpecificationFilters, setAppliedProductSpecificationFilters] = useState<Filters[]>([]);

  const [filters, setFilters] = useState<MainFilters>(
    findDefaultFilterBySelectedCountries(
      pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters,
      defaultFilters,
    ),
  );
  const getInitialTimeFrame = (): Periodicity => {
    const mainFilters =
      defaultFilters &&
      appliedCountries.length !== 0 &&
      defaultFilters.find((e) => e.country === appliedCountries[0].value);

    return getPeriodCountryOnChange(mainFilterStateProducts.timeFrame, mainFilters, pagesConfig.pageName);
  };

  const getSalesType = () => {
    const headerKey = pagesConfig.sortingOptionsProducts.headerKey;
    if (headerKey.toLowerCase().includes('volume')) {
      return SaleType.volume;
    }
    return SaleType.revenue;
  };

  const isCurrentTabLeaderboardTab = () => {
    return selectedLink.value === ProductsTabView.Leaderboard;
  };

  const [selectedGrowthType, setSelectedGrowthType] = useState<GrowthTypes>(mainFilterStateProducts.selectedGrowthType);

  const [timeFrame, setTimeFrame] = useState<Periodicity>(getInitialTimeFrame());

  const [period, setPeriod] = useState<BaseTimePeriod>(
    getInitialPeriod(filters, timeFrame, mainFilterStateProducts.period),
  );

  const [comparisonPeriod, setComparisonPeriod] = useState<RelativePeriod | BaseTimePeriod>(
    mainFilterStateProducts.comparisonPeriod,
  );

  const [shareVsValueVolumeFilterType, setShareVsValueVolumeFilterType] = useState(PerformanceType.Value);

  const [selectedKpi, setSelectedKpi] = useState<KpiTypesOption>({
    label: KpiOptionForLabel.bestSellers,
    value: KpiOptionForValue.bestSellers,
  });
  const [selectedFocusArea, setSelectedFocusArea] = useState<FocusArea>(FocusArea.market);
  const [selectedLineChartSaleType, setSelectedLineChartSaleType] = useState<SaleType>(SaleType.revenue);
  const [selectedRetailerOption, setSelectedRetailerOption] = useState<RetailerKpiOption>(RetailerKpiOption.absolute);

  const isCurrentTabTrendsTab = () => {
    return selectedLink.value === ProductsTabView.Trends;
  };

  const [isTableView, setIsTableView] = useState<boolean>(true);
  const [selectedDisplayType, setSelectedDisplayType] = useState<DisplayOptions>(DisplayOptions.data);
  const [selectedSaleType, setSelectedSaleType] = useState<SaleType>(getSalesType());
  const [currentSortingOption, setCurrentSortingOption] = useState<SortingOptions>(DefaultSortingOptionsForProducts);

  const [loadingOverlay, setLoadingOverlay] = useState<boolean>(false);
  const [showPgModal, setShowPgModal] = useState<boolean>(false);
  const [isTimeFrameChanged, setIsTimeFrameChanged] = useState<boolean>(false);
  const [pgListByCountry, setPgListByCountry] = useState<CountryFilterPair[]>([]);
  const [onPageFirstLoad, setOnPageFirstLoad] = useState<boolean>(true);
  const [onContextBarCompleted, setOnContextBarCompleted] = useState<boolean>(false);

  const { track } = useAnalytics();

  // Initial load
  useEffect(() => {
    setLoadingOverlay(true);
    //Update page viewing
    updateHitlistsRetailerClient({
      ...hitlistsRetailerClientState,
      pageViewing: PageName.RI_PRODUCT,
    });

    const subscriptionPeriodicities: Periodicity[] = getSubscriptionPeriodicitiesByCountryAndProductType(
      effectiveProductType,
      pagesConfig,
      appliedCountries,
    );

    const selectedPgCheckParam: SelectedPgCheckParam = {
      setShowPgModal: setShowPgModal,
      sideFiltersState: sideFiltersState,
      pagesConfig: pagesConfig,
      selectedProductGroup: selectedProductGroup,
      productType: currentProductType,
      productDataType: ProductDataType.ProductAnalyzer,
      pageType: PageType.RI_PRODUCT,
      pageName: PageName.RI_PRODUCT,
      appliedCountries: appliedCountries,
      timeFrame: subscriptionPeriodicities.length === 0 ? subscriptionPeriodicities : [timeFrame],
    };

    checkSelectedPgExists(selectedPgCheckParam);
    getMainFilters();

    //Start Page View Amplitude Tracking
    //Moved Amplitude Tracking to Page level instead of component level
    //Do not load this amplitude tracking on very first load
    if (
      appliedCountries.length !== 0 &&
      selectedProductGroup !== undefined &&
      selectedProductGroup?.length !== 0 &&
      selectedLink.value === ProductsTabView.Overview
    ) {
      const trackingParam: PageViewTrackingFunctionParam = {
        eventTitle: PAGE_VIEW_TRACKING_TITLE,
        sideFilters: sideFiltersState.appliedFilters,
        retailerClientState: hitlistsRetailerClientState,
        track: track,
        pageType: PageType.RI_PRODUCT,
        pageName: PageName.RI_PRODUCT,
        access: pagesConfig.access,
        subSection: SubSection.PRODUCT_OVERVIEW,
        initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.RI_PRODUCT, effectiveProductType),
      };
      pageViewTrackingFunction(trackingParam);
    }
    //End Page View Amplitude Tracking

    setLoadingOverlay(false);
    setOnPageFirstLoad(false);
  }, []);

  useEffect(() => {
    //Start Page View Amplitude Tracking
    //Moved Amplitude Tracking to Page level instead of component level
    //Make initial amplitude load after context bar is completed
    if (onContextBarCompleted && selectedLink.value === ProductsTabView.Overview) {
      const trackingParam: PageViewTrackingFunctionParam = {
        eventTitle: PAGE_VIEW_TRACKING_TITLE,
        sideFilters: sideFiltersState.appliedFilters,
        retailerClientState: hitlistsRetailerClientState,
        track: track,
        pageType: PageType.RI_PRODUCT,
        pageName: PageName.RI_PRODUCT,
        access: pagesConfig.access,
        subSection: SubSection.PRODUCT_OVERVIEW,
        initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.RI_PRODUCT, effectiveProductType),
      };
      pageViewTrackingFunction(trackingParam);
    }
    //refetch save filter when page load finish
    refetch();
    //End Page View Amplitude Tracking
  }, [onPageFirstLoad, onContextBarCompleted]);

  const onFiltersTrayClickHandler = (isExpanded: boolean): void => {
    onFiltersTrayClickHandlerUtils(isExpanded, pagesConfig, removeUnsavedFilters);
  };

  /**
   * To call other APIs when the timeFrame isChanged
   */
  const timeFrameOnChangeHandler = (event: any): void => {
    timeFrameOnChangeHandlerUtils(
      filters,
      setTimeFrame,
      setPeriod,
      mainFilterStateProducts,
      appliedCountries,
      event,
      updateMainFiltersProducts,
    );
  };

  /*
   * Update selectedProductGroup when selectedFilters changes in context
   */
  useEffect(() => {
    updateSelectedProductGroupForProducts(
      pagesConfig,
      selectedCountries,
      setSelectedCountries,
      setSelectedProductGroup,
    );
  }, [pagesConfig.selectedFilters]);

  const factRevenueBySelectedCurrency = useFactRevenueBySelectedCurrency(
    pagesConfig,
    appliedCountries,
    effectiveProductType,
  );

  const renderContent = () => {
    if (!filters && period.value === '') {
      return null;
    }
    //Only render when checking passed, return null if failed
    if (selectedLink.value === ProductsTabView.Overview && showsContent()) {
      // Only render the overview page when mainFilters is being set
      return (
        <Overview
          filters={filters}
          timeFrame={timeFrame}
          period={period}
          comparisonPeriod={comparisonPeriod}
          setTimeFrame={setTimeFrame}
          setPeriod={setPeriod}
          setComparisonPeriod={setComparisonPeriod}
          selectedProductGroup={selectedProductGroup}
          appliedCountries={appliedCountries}
          setSelectedLink={setSelectedLink}
          selectedLink={leaderboardLink}
          timeFrameOnChangeHandler={timeFrameOnChangeHandler}
          pgListByCountry={pgListByCountry}
          factRevenue={factRevenueBySelectedCurrency}
          currentProductType={currentProductType}
          shareVsValueVolumeFilterType={shareVsValueVolumeFilterType}
          setShareVsValueVolumeFilterType={setShareVsValueVolumeFilterType}
          isMultibannerChanging={isMultibannerChanging}
          defaultFilters={defaultFilters}
          prodLeaderboardFeatureProductFilters={appliedProdLeaderboardFeatureProductFilters}
        />
      );
    }
    //Only render when checking passed, return null if failed
    if (selectedLink.value === ProductsTabView.Leaderboard && showsContent()) {
      // Only render the leaderboard page when mainFilters is being set
      return (
        <Leaderboard
          filters={filters}
          timeFrame={timeFrame}
          period={period}
          comparisonPeriod={comparisonPeriod}
          setTimeFrame={setTimeFrame}
          setPeriod={setPeriod}
          setComparisonPeriod={setComparisonPeriod}
          selectedProductGroup={selectedProductGroup}
          appliedCountries={appliedCountries}
          timeFrameOnChangeHandler={timeFrameOnChangeHandler}
          pgListByCountry={pgListByCountry}
          factRevenue={factRevenueBySelectedCurrency}
          currentProductType={currentProductType}
          isTableView={isTableView}
          setIsTableView={setIsTableView}
          selectedDisplayType={selectedDisplayType}
          setSelectedDisplayType={setSelectedDisplayType}
          selectedSaleType={selectedSaleType}
          setSelectedSaleType={setSelectedSaleType}
          currentSortingOption={currentSortingOption}
          setCurrentSortingOption={setCurrentSortingOption}
          isMultibannerChanging={isMultibannerChanging}
          defaultFilters={defaultFilters}
          selectedGrowthType={selectedGrowthType}
          setSelectedGrowthTypes={setSelectedGrowthType}
          prodLeaderboardFeatureProductFilters={appliedProdLeaderboardFeatureProductFilters}
          productSpecificationFilters={appliedProductSpecificationFilters}
          saveViewFilter={getSaveViewFilters()}
          retailerClients={retailerClients}
        />
      );
    }
    //Only render when checking passed, return null if failed
    if (selectedLink.value === ProductsTabView.Trends && showsContent()) {
      // Only render the leaderboard page when mainFilters is being set
      return (
        <Trends
          filters={filters}
          timeFrame={timeFrame}
          period={period}
          comparisonPeriod={comparisonPeriod}
          setTimeFrame={setTimeFrame}
          setPeriod={setPeriod}
          setComparisonPeriod={setComparisonPeriod}
          selectedProductGroup={selectedProductGroup}
          appliedCountries={appliedCountries}
          timeFrameOnChangeHandler={timeFrameOnChangeHandler}
          pgListByCountry={pgListByCountry}
          factRevenue={factRevenueBySelectedCurrency}
          currentProductType={currentProductType}
          selectedKpi={selectedKpi}
          setSelectedKpi={setSelectedKpi}
          selectedFocusArea={selectedFocusArea}
          setSelectedFocusArea={setSelectedFocusArea}
          selectedSaleType={selectedSaleType}
          setSelectedSaleType={setSelectedSaleType}
          selectedLineChartSaleType={selectedLineChartSaleType}
          setSelectedLineChartSaleType={setSelectedLineChartSaleType}
          selectedRetailerOption={selectedRetailerOption}
          setSelectedRetailerOption={setSelectedRetailerOption}
          isMultibannerChanging={isMultibannerChanging}
          defaultFilters={defaultFilters}
          prodLeaderboardFeatureProductFilters={appliedProdLeaderboardFeatureProductFilters}
          productsViewState={getSaveViewFilters()}
          feature={mainFilterState?.feature}
        />
      );
    }
    return null;
  };

  //Group all 'Checking before showing content'
  const showsContent = () => {
    //Add loadingOverlay checking to resolve race condition
    return !isEqual(mainFilterStateProducts, emptyMainFilters) && !showPgModal && !loadingOverlay;
  };

  const appliedPg = useMemo(() => {
    return allFilters.find((e) => e.filterKey === FilterKeys.PRODUCT_GROUP);
  }, [allFilters]);

  /**
   * Get feature values for feature selector by monthly and weekly
   */
  // Retrieve main filters again based on product type - Product Analyzer
  const getMainFilters = async () => {
    const data = await getFilters({
      clientId: currentRetailerClientState.clientId,
      countries: currentRetailerClientState.countries,
      productType: currentProductType,
      productDataType: ProductDataType.ProductAnalyzer,
      productGroups: appliedPg?.options,
    });
    setDefaultFilters(data);
    setFilters(
      findDefaultFilterBySelectedCountries(
        pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters,
        validateFilters(data),
      ),
    );
  };

  useEffect(() => {
    if (selectedProductGroup && selectedProductGroup?.length !== 0 && !showPgModal) {
      // gets the accessible periodicites based on subscription. The subscription here has to be updated to Product Analyzer Subscription once ready
      const accessiblePeriodicities = getSubscriptionPeriodicitiesByCountryAndProductType(
        effectiveProductType,
        pagesConfig,
        selectedCountries,
      );
      if (isTimeFrameChanged) {
        setIsTimeFrameChanged(false);
        return;
      }
      if (accessiblePeriodicities.includes(timeFrame)) {
        getFeatureValuesForProduct();
      }
    }
  }, [selectedProductGroup, selectedCountries]);
  const getFeatureValuesForProduct = async () => {
    if (!selectedProductGroup || selectedProductGroup.length === 0) {
      return;
    }

    const featureValues = await getFeatureValueByPGAndCountry({
      clientId: currentRetailerClientState.clientId,
      productType: effectiveProductType,
      productDataType: ProductDataType.ProductAnalyzer,
      country: selectedCountries.map((countries) => countries.value),
      periodType: timeFrame,
      productGroups: selectedProductGroup?.[0]?.value ? [selectedProductGroup[0].value] : [],
      currentTotalName: TOTAL,
    });

    if (featureValues) {
      // Market definition filters
      productsResponseConfig.response[1].filters = cloneDeep(featureValues.slice(0, 4));

      // Product specification filters
      productsResponseConfig.response[2].filters = cloneDeep(featureValues.slice(4, 5));

      // Product features filters
      productsResponseConfig.response[3].subGroupFilters[0].filters = cloneDeep(featureValues.slice(6));

      setSelectedProdLeaderboardFeatureProductFilters(
        cloneDeep(productsResponseConfig.response[3].subGroupFilters[0].filters),
      );
      setSelectedProductSpecificationFilters(cloneDeep(productsResponseConfig.response[2].filters));
    }

    if (appliedPg !== undefined) {
      pagesConfig.setSelectedPgUpdated(true);
    }
  };

  /*
   * updates selectedProductGroup and appliedCountries when applied filters are changed
   */
  useEffect(() => {
    // Changed to retrieve the state from redux store instead of context
    const appliedFilters = sideFiltersState.appliedFilters[PageType.RI_PRODUCT].selectedFilters;

    if (appliedFilters && appliedFilters.length !== 0) {
      const index = appliedFilters.findIndex((e: any) => e.filterKey === FilterKeys.PRODUCT_GROUP);
      index !== -1 ? setSelectedProductGroup(cloneDeep(appliedFilters[index].options)) : setSelectedProductGroup([]);
    } else {
      setSelectedProductGroup([]);
    }

    /**
     * Use this function to prevent setting the applied/selected countries state when the country
     * `Option` are the same with the currently selected ones.
     *
     * @param currOption - The current applied/selected countries.
     */
    const getEffectiveCountry = (currOption: Option[]) => {
      const initialAppliedCountries = getInitialAppliedCountries(pagesConfig, PageType.RI_PRODUCT, currentProductType);
      return areOptionsTheSame(initialAppliedCountries, appliedCountries, 'value')
        ? currOption
        : initialAppliedCountries;
    };

    setAppliedCountries(getEffectiveCountry);
    setSelectedCountries(getEffectiveCountry);

    updateMainFiltersProducts({
      ...mainFilterStateProducts,
      timeFrame,
      comparisonPeriod,
      country: appliedCountries[0].value,
    });

    // when timeFrame is changed, whatever that is not applied in filter tray will be reset
    const wasDifferent = removeUnsavedFilters(pagesConfig);
    if (wasDifferent) {
      setIsTimeFrameChanged(true);
    }
  }, [timeFrame, selectedLink, pagesConfig.appliedFilters]);

  /*
   * retrieve feature values when applied filters are changed
   */
  useEffect(() => {
    if (
      getSubscriptionPeriodicitiesByCountryAndProductType(
        effectiveProductType,
        pagesConfig,
        selectedCountries,
      ).includes(timeFrame)
    ) {
      getFeatureValuesForProduct();
      setAppliedProdLeaderboardFeatureProductFilters(selectedProdLeaderboardFeatureProductFilters);
      setAppliedProductSpecificationFilters(selectedProductSpecificationFilters);
    }
  }, [timeFrame, pagesConfig.appliedFilters]);

  const resetToLatestPeriodByTimeFrame = () => {
    let mainFilters = defaultFilters.find((e) => e.country === selectedCountries[0].value);

    // additional check to handle scenario where navigating from PA with country A, but country A does not exist at all in Products page
    mainFilters ??= findDefaultFilterBySelectedCountries(
      pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters,
      defaultFilters,
    );

    const isYtdMat = isPeriodYTDOrMAT(period);
    const overviewAndTrendsTabView: string[] = [ProductsTabView.Overview, ProductsTabView.Trends];

    const periodicity = getPeriodCountryOnChange(timeFrame, mainFilters, pagesConfig.pageName);
    const initialPeriod = getCorrespondingLatestPeriod(
      defaultFilters,
      pagesConfig,
      periodicity,
      isCurrentTabLeaderboardTab() || (overviewAndTrendsTabView.includes(selectedLink.value) && isYtdMat)
        ? period
        : undefined,
    );

    setFilters(mainFilters);
    setTimeFrame(periodicity);
    setPeriod(initialPeriod);

    const mainFilterProductsStateUpdate = {
      timeFrame: periodicity,
      period: initialPeriod,
      comparisonPeriod: comparisonPeriod,
      selectedGrowthType: mainFilterStateProducts.selectedGrowthType,
      country: appliedCountries[0].value,
    };
    updateMainFiltersProducts(mainFilterProductsStateUpdate);
  };

  /**
   * Used to reset the main global/redux period to the latest period.
   *
   * This is especially useful when navigating from Leaderboard tab to either the Overview or
   * Trends tab as both the latter pages only displays the latest period. However, this implies
   * that there is no retaining of period states between the tabs.
   */
  useEffect(() => {
    resetToLatestPeriodByTimeFrame();
  }, [selectedLink]);

  /*
   * Used to get main filters for the selected countries when country filter is changed
   */
  useEffect(() => {
    if (selectedCountries[0] !== undefined) {
      const fetchProductGroupForSelectedCountries = async () => {
        const pg = await getProductGroupsByCountry(
          currentRetailerClientState.clientId,
          selectedCountries,
          effectiveProductType,
          ProductDataType.ProductAnalyzer,
        );

        updateCountryAndPGFilters(
          pagesConfig,
          selectedCountries,
          pg,
          productsResponseConfig.response[0],
          currentProductType,
        );
      };

      fetchProductGroupForSelectedCountries();
    }
  }, [selectedCountries]);

  useEffect(() => {
    if (appliedCountries[0] !== undefined) {
      resetToLatestPeriodByTimeFrame();
      const fetchProductGroupForAppliedCountries = async () => {
        const pg = await getProductGroupsByCountry(
          currentRetailerClientState.clientId,
          appliedCountries,
          effectiveProductType,
          ProductDataType.ProductAnalyzer,
        );

        setPgListByCountry(pg);
      };

      pagesConfig.setSelectedPgUpdated(true);

      setAppliedProdLeaderboardFeatureProductFilters(selectedProdLeaderboardFeatureProductFilters);
      setAppliedProductSpecificationFilters(selectedProductSpecificationFilters);
      fetchProductGroupForAppliedCountries();
    }
  }, [appliedCountries]);

  const onChangeHandler = async (subscriptions: ClientSubscription[]) => {
    setLoadingOverlay(true);
    setIsMultibannerChanging(true);
    setIsMultibannerStatesUpdated(false);
    // Default clientId, but the clientId should be always the same no matter how many countries the client has
    const defaultClientId = subscriptions[0].retailer;

    const subscribedCountries = mapSubscribedCountries(subscriptions);

    // Display saved filter according to selected retailer for both product types
    await getAndStoreSavedFilters(currentProductType, defaultClientId, pagesConfig, pagesConfig.userId);

    // Default country, retain the countries selection if exists in the subscription
    const defaultCountry = mapDefaultSubscribedCountry(subscriptions, selectedCountries);
    const defaultCountryCode = defaultCountry.countryCode;

    // get RI subscription. current assumption is that it exist
    const retailInsightsSubscriptions = getSubscriptionForOtherProducts(
      isAuthorizedRetailKadUser ? ProductType.RetailKad : ProductType.PerformanceAnalyzer,
      pagesConfig,
      defaultClientId,
    );
    const retailInsightsSubscribedCountries = mapSubscribedCountries(retailInsightsSubscriptions);

    // Get the current side menu filters selection from pagesConfig
    const pagesConfigAllFilters = [...pagesConfig.appliedFilters];

    //Retrieve client name
    const selectedClientName = getSelectedClientNameWithClientId(retailerClients, defaultClientId);

    if (retailInsightsSubscribedCountries.length !== 0) {
      const onChangeMethodParam: OnRetailerChangeParam = {
        subscriptions: retailInsightsSubscriptions,
        appliedCountries,
        defaultClientId,
        pagesConfig,
        productType: ProductType.PerformanceAnalyzer,
        productDataType: ProductDataType.PerformanceAnalyzer,
        subscribedCountries: retailInsightsSubscribedCountries,
        setLoadingOverlay: (_) => {},
        timeFrame,
        pagesConfigAllFilters,
        pageName: PageName.RI_PRODUCT,
        pageList: [
          PageType.RETAILER_OVERVIEW,
          PageType.CATEGORY_LEADERBOARD,
          PageType.OPPORTUNITY_REVIEW,
          PageType.PERFORMANCE_ANALYSIS,
        ],
      };
      await onChangeHandlerForOtherProducts(onChangeMethodParam).then((response) => {
        if (!response) {
          setIsMultibannerStatesUpdated(true);
          return;
        }
        updateInitialFilters({ initialFilters: response.filtersData });
        updateMainFiltersRO({
          timeFrame: response.currTimeFrame,
          period: response.currPeriod,
          country: response.defaultCountryCode,
        });
        updateRetailerClient(
          constructRetailerClientState(defaultClientId, retailInsightsSubscribedCountries, selectedClientName),
        );
      });
    }

    // Call filters API for Product Analyzer
    const filtersDataProducts = await getFiltersForClient(
      defaultClientId,
      subscribedCountries,
      (_) => {},
      currentProductType,
      ProductDataType.ProductAnalyzer,
    );

    setDefaultFilters(filtersDataProducts);

    const filtersProductsDataBasedOnCountry = filtersDataProducts.find(
      (filter) => filter.country === defaultCountryCode,
    );

    if (!filtersProductsDataBasedOnCountry) {
      saveErrorLogForOnChange(pagesConfig.hitlistsClientCountries, pagesConfig.appliedFilters, PageName.RI_PRODUCT);
      setIsMultibannerStatesUpdated(true);
      return;
    }

    constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultCountry, [PageType.RI_PRODUCT]);

    // Get time frame and period for Product Analyzer
    const currTimeFrameProducts = retainTimeFrame(filtersProductsDataBasedOnCountry, timeFrame);
    const currPeriodProducts = retainTimePeriod(filtersProductsDataBasedOnCountry, currTimeFrameProducts);

    const mainFilterProductsStateUpdate = {
      timeFrame: currTimeFrameProducts,
      period: currPeriodProducts,
      comparisonPeriod: comparisonPeriod,
      selectedGrowthType: mainFilterStateProducts.selectedGrowthType,
      country: defaultCountryCode,
    };

    const storeFilterSettings = {
      appliedFilters: pagesConfigAllFilters,
    };

    updateSideFilters(storeFilterSettings);
    updateMainFiltersProducts(mainFilterProductsStateUpdate);
    updateHitlistsRetailerClient(
      constructRetailerClientState(
        defaultClientId,
        subscribedCountries,
        selectedClientName,
        hitlistsRetailerClientState.pageViewing,
      ),
    );
    updateSellerInsightsRetailerClient(
      constructRetailerClientState(
        defaultClientId,
        subscribedCountries,
        selectedClientName,
        hitlistsRetailerClientState.pageViewing,
      ),
    );
    updateInitialFiltersProducts({ initialFilters: filtersDataProducts });

    const productGroup = getProductGroupFromSideFilters(sideFiltersState.appliedFilters[PageType.OPPORTUNITY_REVIEW]);

    if (productGroup) {
      const featureValues = await getFeatureValueForRetailer({
        clientId: defaultClientId,
        productType: effectiveProductType,
        productDataType: ProductDataType.ProductAnalyzer,
        selectedCountries: constructCountryOption(defaultCountry),
        timeFrame: currTimeFrameProducts,
        selectedProductGroup: productGroup.options[0].value,
        currentTotalName: TOTAL,
      } as RetailerDetails);

      updateMainFilters({ feature: getFeatureSelector(featureValues, mainFilterState.feature) });
    }

    /**
     * The reset sequence below is very important, this is due to the massive useEffect that we have in the page
     */
    // Do not simply move the setState below, it will trigger unintended side effects
    if (retailInsightsSubscriptions.length !== 0) {
      pagesConfig.setClientCountries(retailInsightsSubscribedCountries);
      pagesConfig.setClientId(defaultClientId);
    }
    pagesConfig.setHitlistsClientId(defaultClientId);
    pagesConfig.setSellerInsightsClientId(defaultClientId);
    pagesConfig.setHitlistsClientCountries(subscribedCountries);
    pagesConfig.setSellerInsightsClientCountries(subscribedCountries);

    setSelectedCountries(constructCountryOption(defaultCountry));

    pagesConfig.setSelectedFilters(pagesConfigAllFilters);

    setFilters(filtersProductsDataBasedOnCountry);

    setAppliedCountries(constructCountryOption(defaultCountry));

    setPeriod(currPeriodProducts);
    setTimeFrame(currTimeFrameProducts);

    //For product page useEffect listener
    pagesConfig.setIsRetailerChanged(true);
    setIsMultibannerStatesUpdated(true);
    await refetch();
    setLoadingOverlay(false);
  };

  /**
   * This useEffect is used to set isMultibannerChanging to false only when isMultibannerStatesUpdated is set to true at the end of the onChangeHandler
   * This to ensure that all of the states that are being updated in onChangeHandler has been updated, as updating states are done asynchronously
   */
  useEffect(() => {
    if (isMultibannerStatesUpdated) {
      setIsMultibannerChanging(false);
    }
  }, [isMultibannerStatesUpdated]);

  useEffect(() => {
    if (appliedProdLeaderboardFeatureProductFilters.length < 1) {
      setAppliedProdLeaderboardFeatureProductFilters(selectedProdLeaderboardFeatureProductFilters);
    }
  }, [selectedProdLeaderboardFeatureProductFilters]);

  useEffect(() => {
    if (appliedProductSpecificationFilters.length < 1) {
      setAppliedProductSpecificationFilters(selectedProductSpecificationFilters);
    }
  }, [selectedProductSpecificationFilters]);

  useEffect(() => {
    //if previously selected sorting direction is asc, change to desc if status is disabled
    updateSortingOptions(appliedProductSpecificationFilters, pagesConfig, currentSortingOption);
  }, [appliedProductSpecificationFilters]);

  const getSaveViewFilters = (): ProductsViewState => {
    const { periodicity, matYtdLabel } = getPeriodicityForMATAndYTD(period, timeFrame);
    return {
      periodicity: periodicity,
      matYtdTimePeriodLabel: matYtdLabel.length > 0 ? matYtdLabel : undefined,
      comparisonPeriod:
        comparisonPeriod === RelativePeriod.PreviousPeriod
          ? RelativePeriodLabel.PreviousPeriod
          : RelativePeriodLabel.PreviousYearCurrentPeriod,
      growthType: capitalizeFirstLetter(mainFilterStateProducts.selectedGrowthType),
      appliedFilters: pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters,
      retailerClientState: retailerClientState,
      initialFilterState: initialFiltersState,
      appliedCountries: appliedCountries,
      hitlistRetailerClientState: hitlistsRetailerClientState,
      hitlistInitialFilterState: initialFiltersProductsState,
      productMainFilterState: mainFilterStateProducts,
      sorting: pagesConfig.sortingOptionsProducts,
      overviewShareVsValueVolumeFilterType:
        selectedLink.value === ProductsTabView.Overview ? shareVsValueVolumeFilterType : null,
      trendsSelectedKpi: isCurrentTabTrendsTab() ? selectedKpi : null,
      trendsSelectedFocusArea: isCurrentTabTrendsTab() ? selectedFocusArea : null,
      trendsSelectedSaleType: isCurrentTabTrendsTab() ? selectedSaleType : null,
      trendsSetSelectedLineChartSaleType: isCurrentTabTrendsTab() ? selectedLineChartSaleType : null,
      trendsSelectedRetailerOption: isCurrentTabTrendsTab() ? selectedRetailerOption : null,
      leaderboardIsTableView: isCurrentTabLeaderboardTab() ? isTableView : null,
      leaderboardSelectedDisplayType: isCurrentTabLeaderboardTab() ? selectedDisplayType : null,
      leaderboardSelectedSaleType: isCurrentTabLeaderboardTab() ? selectedSaleType : null,
      leaderboardSelectedSortingOption: isCurrentTabLeaderboardTab() ? currentSortingOption : null,
    };
  };

  // props to pass local setState to restore saved views hooks
  const restoreLocalViewProps: ProductTabRestoreLocalSavedViewProps = {
    productType: effectiveProductType,
    selectedLink: selectedLink,
    setIsSubscribed,
    setTimeFrame,
    setPeriod,
    setFilters,
    setSelectedCountries,
    setAppliedCountries,
    setDefaultFilters,
    setSelectedProductGroupOptions: setSelectedProductGroup,
    setAppliedProductGroupOptions: setSelectedProductGroup,
    setOverviewShareVsValueVolumeFilterType: setShareVsValueVolumeFilterType,
    setTrendsSelectedKpi: setSelectedKpi,
    setTrendsSelectedFocusArea: setSelectedFocusArea,
    setTrendsSelectedSaleType: setSelectedSaleType,
    setTrendsSetSelectedLineChartSaleType: setSelectedLineChartSaleType,
    setTrendsSelectedRetailerOption: setSelectedRetailerOption,
    setLeaderboardIsTableView: setIsTableView,
    setLeaderboardSelectedDisplayType: setSelectedDisplayType,
    setLeaderboardSelectedSaleType: setSelectedSaleType,
    setLeaderboardSelectedSortingOption: setCurrentSortingOption,
    setShowPgModal,
    setRelativePeriod: setComparisonPeriod,
    setGrowthOption: setSelectedGrowthType,
  };

  const resetSideFilter = () => {
    removeUnsavedFilters(pagesConfig);
    pagesConfig.setClearFilters(false);
    pagesConfig.setApplyFilters(false);
  };

  // Call restore saved views hooks to restore the Product page views
  useRestoreProductsSavedView(restoreLocalViewProps);

  const getSaveViewTrackingObject = () => {
    return {
      access: pagesConfig.access,
      locationId: appliedCountries.map((country) => country.label),
      pageName: PageName.RI_PRODUCT,
      itemGroup: selectedProductGroup?.map((pg) => pg.label).toString(),
      section: TrackingGeneralProp.SECTION_TRACKING_PROP,
      subSection: PageName.RI_PRODUCT,
    };
  };

  return (
    // @ts-ignore
    <div
      key={`${pagesConfig.pageName}-${pgListByCountry}-${currentProductType}`} //NOSONAR typescript:S6551
    >
      <Page>
        <Page.Title>
          <div className="pt-8">
            <PageHeader
              retailerClients={retailerClients}
              onChangeHandler={onChangeHandler}
              productType={currentProductType}
            >
              <div className="grid grid-cols-3">
                <div className="col-start-1">
                  <h1 className="text-heading text-4xl font-normal m-0">
                    <span>Products</span>
                    <PageInfo title="Products" content={<TooltipsTextComponent />} />
                  </h1>
                </div>
                <div className="col-start-5">
                {isAuthorizedUser && isSubscribed && !onPageFirstLoad && (
                  <div className="flex justify-end ml-auto">
                    <ProductsCopyViewButton
                      productsViewState={getSaveViewFilters()}
                      copyViewTracking={getSaveViewTrackingObject()}
                    />
                    <FilterToggleButton onClick={onFiltersTrayClickHandler} testId="filter-button" />
                  </div>
                )}
              </div>
              </div>
            </PageHeader>
          </div>
        </Page.Title>
        <Page.Content>
          <InfoModal />
          <TabNav
            selectedLink={selectedLink}
            links={productsLinkConfig}
            setSelectedLink={setSelectedLink}
            preNavigateAction={resetSideFilter}
          />
          <LoadingOverlay loadingOverlay={loadingOverlay} />
          {isAuthorizedUser && isSubscribed && !onPageFirstLoad ? (
            <>
              <ProductsModelCompareProvider>
                <Suspense fallback={<feedback.DotsLoader />}>{renderContent()}</Suspense>
              </ProductsModelCompareProvider>
              <br />
              {showPgModal && (
                <ContextBar
                  setSelectedProductGroup={(productPg: Option): void => setSelectedProductGroup([productPg])}
                  setSelectedCountries={setSelectedCountries}
                  isShown={showPgModal}
                  setShowPgModal={(isShowPgModal: SetStateAction<boolean>) => setShowPgModal(isShowPgModal)}
                  initialFilters={initialFiltersState.initialFilters}
                  initialFiltersProducts={defaultFilters}
                  loadingFeatureValue={false}
                  productType={currentProductType}
                  productDataType={ProductDataType.ProductAnalyzer}
                  //add this to prevent amplitude race condition
                  setApplyContext={setOnContextBarCompleted}
                />
              )}
            </>
          ) : (
            Unauthorized()
          )}
        </Page.Content>
        <Page.Footer> </Page.Footer>
      </Page>
    </div>
  );
};

export default ProductAnalyzer;
